import React from "react";
import { MapPin, Check, Phone } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { BRAND } from "@/lib/brand";
import { TID } from "@/constants/testIds/dv";

// Broad Google Maps embed centered on Eastern PA / NJ tri-state (no API key required)
const MAP_EMBED_SRC =
  "https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d397584.0!2d-75.6!3d40.35!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sus!4v1700000000000";

export const ServiceArea = () => {
  const { t } = useLanguage();

  return (
    <section
      id="area"
      data-testid={TID.serviceAreaSection}
      className="bg-[#0A0A0A] text-white py-24 md:py-32 relative overflow-hidden"
    >
      <div className="container-x grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-[#E63946]" />
            <span className="label-eyebrow">{t("service_area.eyebrow")}</span>
          </div>
          <h2 className="font-display uppercase text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[0.95]">
            {t("service_area.title")}
          </h2>
          <p className="mt-6 text-white/75 leading-relaxed">{t("service_area.subtitle")}</p>

          <ul className="mt-8 space-y-3">
            {["service_area.bullet_primary", "service_area.bullet_secondary", "service_area.bullet_travel"].map(
              (k) => (
                <li key={k} className="flex items-start gap-3">
                  <div className="h-5 w-5 mt-0.5 grid place-items-center bg-[#E63946] rounded-sm shrink-0">
                    <Check className="h-3.5 w-3.5 text-white" />
                  </div>
                  <span className="text-sm text-white/85">{t(k)}</span>
                </li>
              )
            )}
          </ul>

          <div className="mt-8 border border-white/10 p-5 rounded-sm flex items-center justify-between gap-4">
            <div className="text-sm text-white/80">{t("service_area.cta")}</div>
            <a
              href={BRAND.phoneHref}
              className="inline-flex items-center gap-2 text-[#E63946] font-display text-xl font-bold whitespace-nowrap"
            >
              <Phone className="h-5 w-5" />
              {BRAND.phone}
            </a>
          </div>
        </div>

        <div className="lg:col-span-3 relative">
          {/* Decorative frame */}
          <div className="absolute -top-3 -left-3 h-16 w-16 border-t-2 border-l-2 border-[#E63946] pointer-events-none" />
          <div className="absolute -bottom-3 -right-3 h-16 w-16 border-b-2 border-r-2 border-[#E63946] pointer-events-none" />

          <div className="relative aspect-[4/3] md:aspect-[16/10] w-full border border-white/15 rounded-sm overflow-hidden bg-black">
            <iframe
              title="DV Solutions service area"
              data-testid={TID.serviceAreaMap}
              src={MAP_EMBED_SRC}
              className="w-full h-full grayscale-[40%] contrast-110"
              style={{ border: 0, filter: "grayscale(35%) contrast(1.05) invert(0)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
            {/* Center pin overlay */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="relative">
                <div className="absolute inset-0 animate-ping rounded-full bg-[#E63946]/40 h-12 w-12 -translate-x-1/2 -translate-y-1/2 left-1/2 top-1/2" />
                <div className="relative h-12 w-12 grid place-items-center bg-[#E63946] text-white rounded-full shadow-lg ring-4 ring-black/60">
                  <MapPin className="h-6 w-6" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceArea;
